import { ApiService, ResponseData } from './api'

export interface AutoPlaceData {
	id?: number
	space_id?: number
	module_key?: string
	title: string
	description?: string | null
	start_step_id?: string | null
	document_json?: string | null
	presentation_json?: string | null
	revision?: number
	publish_status_key?: string | null
	published_at?: string | null
	publish_error_text?: string | null
	created_at?: string
	updated_at?: string
}

export interface AutoPlaceUpdateData {
	title?: string
	description?: string | null
	document_json?: unknown | null
}

export interface AutoDraftValidationIssue {
	level: string
	code?: string
	entityType?: string
	entityId?: string
	operationType?: string
	moduleKey?: string
	path?: string
	message: string
}

export interface AutoDraftValidationReport {
	valid: boolean
	issueCount: number
	errorCount: number
	warningCount: number
	checkedAt: string
	issues: AutoDraftValidationIssue[]
}

export interface AutoRuntimePlaneSyncReport {
	requested?: boolean
	status?: string
	reason?: string | null
	counts?: Record<string, unknown> | null
	place?: Record<string, unknown> | null
	_runtime_http_status?: number | null
}

export interface AutoProjectionReport {
	root_entity_type?: string
	root_entity_id?: string
	entities_count?: number
	transitions_count?: number
	stage_live_verification?: {
		ok?: boolean
		entities_count?: number
		transitions_count?: number
	}
	runtime_plane_sync?: AutoRuntimePlaneSyncReport | null
	created_at?: string
	[key: string]: unknown
}

export interface AutoOperationTypeDescriptor {
	type: string
	operation_type_key?: string
	title?: string
	description?: string
	descriptor_revision?: number
	input_schema?: Record<string, unknown>
	instrument_key?: string
	module_key?: string
	native_action?: string
	preferred_target?: string
	legacy_target?: string
	result_types?: string[]
	supported_methods?: string[]
	[key: string]: unknown
}

export interface AutoTargetPlace {
	id: number
	space_id: number
	title: string
	description?: string | null
	revision: number
	publish_status_key?: string | null
	published_at?: string | null
	publish_error_text?: string | null
	steps_count: number
	operations_count: number
	event_handlers_count: number
}

export interface AutoTargetStep {
	place_id: number
	step_id: string
	title: string
	description?: string | null
	start_operation_id?: string | null
	default_operation_id?: string | null
	pos_x: number
	pos_y: number
	sort_order: number
}

export interface AutoTargetOperation {
	place_id: number
	step_id: string
	operation_id: string
	type_key: string
	title: string
	description?: string | null
	next_operation_id?: string | null
	sort_order: number
}

export interface AutoEntityTargets {
	index_status: 'ready' | 'missing' | string
	filters: {
		place_id?: string | null
		step_id?: string | null
	}
	places: AutoTargetPlace[]
	steps: AutoTargetStep[]
	operations: AutoTargetOperation[]
}

export interface AutoPublishResult {
	success: boolean
	publish_job?: Record<string, unknown>
	validation_report?: AutoDraftValidationReport | Record<string, unknown> | null
	projection_report?: AutoProjectionReport | null
	cleanup?: Record<string, unknown>
	message?: string
	error?: string
}

export class AutoService {

	private readonly apiService: ApiService
	private readonly cpDomain: string

	constructor(apiService: ApiService)
	{
		if (!apiService)
			throw new Error('ApiService не инициализирован')
		if (!apiService.getConfig()?.cp_domain)
			throw new Error('Отсутствует CP-домен в конфигурации сервера')

		this.apiService = apiService
		this.cpDomain = apiService.getConfig().cp_domain
	}

	async getPlaces(spaceId: string | number): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, '/places', 'GET')
	}

	async createPlace(spaceId: string | number, placeData: Pick<AutoPlaceData, 'title' | 'description'>): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, '/places', 'POST', placeData)
	}

	async getPlace(spaceId: string | number, placeId: string | number): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, `/place/${placeId}`, 'GET')
	}

	async updatePlace(spaceId: string | number, placeId: string | number, placeData: AutoPlaceUpdateData): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, `/place/${placeId}`, 'PATCH', placeData)
	}

	async savePlaceDraft(spaceId: string | number, placeId: string | number, draftDocument: unknown): Promise<ResponseData>
	{
		return this.updatePlace(spaceId, placeId, {
			document_json: draftDocument
		})
	}

	async validatePlaceDraft(spaceId: string | number, placeId: string | number, draftDocument: unknown): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, `/place-validate/${placeId}`, 'POST', {
			document_json: draftDocument
		})
	}

	async publishPlace(spaceId: string | number, placeId: string | number): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, `/place-publish/${placeId}`, 'POST', {})
	}

	async getOperationTypes(spaceId: string | number): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, '/operation-types', 'GET')
	}

	async getEntityTargets(
		spaceId: string | number,
		filters: { place_id?: string | number | null; step_id?: string | number | null } = {}
	): Promise<ResponseData>
	{
		const params = new URLSearchParams()
		if (filters.place_id !== undefined && filters.place_id !== null && filters.place_id !== '')
			params.set('place_id', String(filters.place_id))
		if (filters.step_id !== undefined && filters.step_id !== null && filters.step_id !== '')
			params.set('step_id', String(filters.step_id))

		const query = params.toString()
		return this.fetchAuto(spaceId, `/entity-targets${query ? `?${query}` : ''}`, 'GET')
	}

	async deletePlace(spaceId: string | number, placeId: string | number): Promise<ResponseData>
	{
		return this.fetchAuto(spaceId, `/place/${placeId}`, 'DELETE')
	}

	private async fetchAuto(
		spaceId: string | number,
		path: string,
		method: string,
		body: unknown = undefined
	): Promise<ResponseData> {
		try {
			const response = await this.apiService.doFetch(
				this.cpDomain,
				`/${spaceId}/auto/s${path}`,
				method,
				body
			)

			return await this.apiService.getResponseJson(response)
		} catch (error) {
			return {
				success: false,
				error: error instanceof Error ? error.message : 'Ошибка запроса Auto API'
			}
		}
	}
}
